import type { CreateMemoryInput, MemoryWithMedia } from "@memory-universe/domain";
import { create } from "zustand";
import { localMemoryRepository } from "../data/localMemoryRepository";
import { supabaseMemoryRepository } from "../data/supabaseMemoryRepository";
import { isSupabaseConfigured } from "../../../shared/supabase/client";
import { releaseMemoryUrls, type RecentlyDeletedItem } from "../data/memoryRepository";
import { useSpaceStore } from "../../spaces/model/spaceStore";
import { samplePhotoPalette } from "../../../shared/media/photoPalette";
import { prepareMemoryMedia } from "../../../shared/platform/localOriginalMedia";

type LoadStatus = "idle" | "loading" | "ready" | "error";

interface CreateMemoryPayload {
  input: CreateMemoryInput;
  files: File[];
}

interface MemoryState {
  memories: MemoryWithMedia[];
  status: LoadStatus;
  error: string | null;
  loadedAt: number | null;
  recentlyDeleted: RecentlyDeletedItem[];
  recentlyDeletedStatus: LoadStatus;
  recentlyDeletedError: string | null;
  loadMemories: () => Promise<void>;
  loadRecentlyDeleted: () => Promise<void>;
  createMemory: (payload: CreateMemoryPayload) => Promise<MemoryWithMedia>;
  deleteMemory: (memoryId: string) => Promise<void>;
  removeMemoryMedia: (memoryId: string, mediaId: string) => Promise<void>;
  restoreMemory: (memoryId: string) => Promise<void>;
  restoreMemoryMedia: (memoryId: string, mediaId: string) => Promise<void>;
}

let loadGeneration = 0;

function errorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object") {
    const value = error as { code?: unknown; message?: unknown };
    if (value.code === "PGRST204" || value.code === "42703") return "云端数据库正在更新，请稍后刷新页面";
    if (typeof value.message === "string" && value.message) return value.message;
  }
  return "记忆星河暂时无法读取数据";
}

const repository = isSupabaseConfigured ? supabaseMemoryRepository : localMemoryRepository;

export const useMemoryStore = create<MemoryState>((set, get) => ({
  memories: [],
  status: "idle",
  error: null,
  loadedAt: null,
  recentlyDeleted: [],
  recentlyDeletedStatus: "idle",
  recentlyDeletedError: null,

  loadMemories: async () => {
    const galaxy = useSpaceStore.getState().space;
    if (!galaxy) {
      loadGeneration += 1;
      releaseMemoryUrls(get().memories);
      set({ memories: [], status: "ready", error: null, loadedAt: null });
      return;
    }

    const generation = ++loadGeneration;
    set({ status: "loading", error: null });
    try {
      const nextMemories = await repository.list(galaxy.id);
      if (generation !== loadGeneration) {
        releaseMemoryUrls(nextMemories);
        return;
      }
      releaseMemoryUrls(get().memories);
      set({ memories: nextMemories, status: "ready", loadedAt: Date.now() });
      useSpaceStore.getState().updateLocalGalaxyStats(
        galaxy.id,
        nextMemories.map((item) => ({ occurredAt: item.memory.occurredAt }))
      );
    } catch (error) {
      if (generation === loadGeneration) set({ status: "error", error: errorMessage(error) });
    }
  },

  loadRecentlyDeleted: async () => {
    const galaxy = useSpaceStore.getState().space;
    if (!galaxy) {
      set({ recentlyDeleted: [], recentlyDeletedStatus: "ready", recentlyDeletedError: null });
      return;
    }
    set({ recentlyDeletedStatus: "loading", recentlyDeletedError: null });
    try {
      const recentlyDeleted = await repository.listRecentlyDeleted(galaxy.id);
      set({ recentlyDeleted, recentlyDeletedStatus: "ready", recentlyDeletedError: null });
    } catch (error) {
      set({ recentlyDeletedStatus: "error", recentlyDeletedError: errorMessage(error) });
    }
  },

  createMemory: async ({ input, files }) => {
    const galaxy = useSpaceStore.getState().space;
    if (!galaxy) throw new Error("请先进入一条记忆星河");
    const palettePromise = files[0] ? samplePhotoPalette(files[0]) : Promise.resolve(null);
    const media = await prepareMemoryMedia(files);
    const created = await repository.create({ galaxyId: galaxy.id, input, media });
    const palette = await palettePromise;
    const nextMemories = [...get().memories, created].toSorted(
      (left, right) => Date.parse(left.memory.occurredAt) - Date.parse(right.memory.occurredAt)
    );
    set({ memories: nextMemories, error: null, status: "ready" });
    useSpaceStore.getState().updateLocalGalaxyStats(
      galaxy.id,
      nextMemories.map((item) => ({ occurredAt: item.memory.occurredAt }))
    );
    if (palette) useSpaceStore.getState().updateGalaxyVisualPalette(galaxy.id, palette);
    return created;
  },

  deleteMemory: async (memoryId) => {
    await repository.softDeleteMemory(memoryId);
    await get().loadMemories();
    await get().loadRecentlyDeleted();
  },

  removeMemoryMedia: async (memoryId, mediaId) => {
    await repository.softDeleteMemoryMedia(memoryId, mediaId);
    await get().loadMemories();
    await get().loadRecentlyDeleted();
  },

  restoreMemory: async (memoryId) => {
    await repository.restoreMemory(memoryId);
    await Promise.all([get().loadMemories(), get().loadRecentlyDeleted()]);
  },

  restoreMemoryMedia: async (memoryId, mediaId) => {
    await repository.restoreMemoryMedia(memoryId, mediaId);
    await Promise.all([get().loadMemories(), get().loadRecentlyDeleted()]);
  }
}));
